import { makeAutoObservable } from 'mobx';

export interface IProfilProfessionalStore {
    id?: string
    firstName: string;
    lastName: string;
    email?: string;
    phone: string;
    profession: string;
    timeZone?: string;
    language?: string;
    emailVerified?: boolean;
    onboarding?: boolean;
    picture?: string | null;
    stripeConnectedAccountId?: string | null;
    stripeAccountStatus?: 'not_started' | 'pending' | 'verified' | 'restricted';
    absorbStripeFees?: boolean;
}

export class ProfessionalStore {
    profileProfessional: IProfilProfessionalStore | null = null

    constructor() {
        makeAutoObservable(this);
    }

    // setter
    setProfileProfessional(profileProfessional: IProfilProfessionalStore | null): void {
        this.profileProfessional = profileProfessional
    }

    /**
     * Met à jour le statut d'onboarding dans le store
     * @param onboardingStatus Nouveau statut d'onboarding
     */
    setOnboarding(onboardingStatus: boolean): void {
        if (this.profileProfessional) {
            this.profileProfessional = {
                ...this.profileProfessional,
                onboarding: onboardingStatus
            };
        }
    }

    /**
     * Met à jour la photo de profil dans le store
     * @param pictureUrl URL de la photo de profil
     */
    setProfessionalPicture(pictureUrl: string): void {
        if (this.profileProfessional) {
            this.profileProfessional = {
                ...this.profileProfessional,
                picture: pictureUrl
            };
        }
    }

    // getter
    get getProfileProfessional(): IProfilProfessionalStore | null {
        return this.profileProfessional
    }

    get getId() {
        return this.profileProfessional?.id
    }

    get getInitialProfil() {
        if (!this.profileProfessional) {
            return '';
        }

        const { firstName, lastName } = this.profileProfessional;

        // Prendre juste la première lettre du premier prénom
        const firstNameInitial = firstName.trim().charAt(0).toUpperCase();

        // Prendre juste la première lettre du nom
        const lastNameInitial = lastName.trim().charAt(0).toUpperCase();

        return `${firstNameInitial}${lastNameInitial}`;
    }

    /**
     * Vérifie si le compte Stripe Connect est configuré et vérifié
     * @returns true si le compte est vérifié, false sinon
     */
    get isStripeConnected(): boolean {
        return this.profileProfessional?.stripeAccountStatus === 'verified';
    }

    /**
     * Vérifie si le professionnel a besoin de configurer Stripe Connect
     * @returns true si la configuration est nécessaire (not_started, pending ou restricted)
     */
    get needsStripeConfiguration(): boolean {
        const status = this.profileProfessional?.stripeAccountStatus;
        return status === 'not_started' || status === 'pending' || status === 'restricted' || !status;
    }

}

// Instance singleton du ProfessionalStore
const professionalStore = new ProfessionalStore();
export default professionalStore;